import { PeopleOutline, RemoveCircleOutline, Search, FilterAltOff } from '@mui/icons-material'
import {
    Box,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material'
import { DataGrid, GridActionsCellItem, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid'
import { useEffect, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Button } from 'src/components/forms'
import { ControlledAutocomplete } from 'src/components/forms/ControlledAutocomplete'
import { HttpCacheUser } from 'src/http/cache/user/httpCacheUser'
import { HttpGroup } from 'src/http/group/httpGroup'
import { GroupDTOFields, GroupUserFields, GroupUserViewDTO, GroupViewDTO } from 'src/types/group/groupData'
import { GroupUserFilterDTOFields } from 'src/types/user/userData'
import { EntityWithIdFields } from 'src/types'
import { z } from 'zod'

export type EditGroupUsersProps = {
    group: GroupViewDTO
    onClose: () => void
}

const opcionActivo = { value: 1, label: 'Activos', color: 'success' as const }
const opcionTodos = { value: 2, label: 'Todos', color: 'info' as const }
const toggleActivoOptions = [opcionActivo, opcionTodos]

const schemaFiltro = z.object({
    [GroupUserFilterDTOFields.Nombre]: z.string().optional(),
    [GroupUserFilterDTOFields.Mail]: z.string().optional(),
    [GroupUserFilterDTOFields.CodsCargo]: z.array(z.number()).optional(),
    [GroupUserFilterDTOFields.SoloActivos]: z.boolean().optional()
})

type SchemaFiltro = z.infer<typeof schemaFiltro>

function EditGroupUsers({ group, onClose }: EditGroupUsersProps) {
    const [users, setUsers] = useState<GroupUserViewDTO[]>()
    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>()
    const [activoFilter, setActivoFilter] = useState<number[]>([opcionActivo.value])

    const { control, handleSubmit, reset: resetForm } = useForm<SchemaFiltro>({
        defaultValues: {
            [GroupUserFilterDTOFields.SoloActivos]: true
        }
    })

    const watchSoloActivos = useWatch({ control, name: GroupUserFilterDTOFields.SoloActivos })

    useEffect(() => {
        handleSubmit(fetchUsers)()
    }, [watchSoloActivos])

    useEffect(() => {
        handleSubmit(fetchUsers)()
    }, [group])

    const fetchUsers = (data?: SchemaFiltro) => {
        const soloActivos = activoFilter.includes(opcionActivo.value) ? true : undefined

        return HttpGroup.getUsersByGroup(group[EntityWithIdFields.Id], {
            ...data,
            [GroupUserFilterDTOFields.SoloActivos]: soloActivos
        }).then(users => {
            setUsers(users)
            const assigned = users
                .filter(u => u[GroupUserFields.GroupAssigned])
                .map(u => u[GroupUserFields.UserId])
            setRowSelectionModel(prev => [...new Set([...assigned, ...(prev || [])])])
        })
    }

    const onFilterChange = () => {
        handleSubmit(fetchUsers)()
    }

    const handleRemoveUser = (userId: number) => {
        setRowSelectionModel(prev => (prev ?? []).filter(id => id !== userId))
    }

    const handleActivoChange = (_: any, newValue: number | null) => {
        if (newValue === null) return
        setActivoFilter([newValue])
    }

    useEffect(() => {
        handleSubmit(fetchUsers)()
    }, [activoFilter])

    const onSubmitWrapper = () => {
        let mensajeError = 'No se pudieron guardar los usuarios'

        const promise = HttpGroup.updateGroupUsers(group[EntityWithIdFields.Id], (rowSelectionModel as number[]) || [])
            .then(() => {
                setTimeout(onClose, 500)
            })
            .catch(err => {
                mensajeError = err?.message || mensajeError
                throw err
            })

        toast.promise(promise, {
            success: 'Usuarios actualizados',
            error: () => mensajeError,
            loading: 'Guardando'
        })

        return promise
    }

    const handleClearFilters = () => {
        resetForm({
            [GroupUserFilterDTOFields.Nombre]: undefined,
            [GroupUserFilterDTOFields.Mail]: undefined,
            [GroupUserFilterDTOFields.CodsCargo]: undefined,
            [GroupUserFilterDTOFields.SoloActivos]: undefined
        })
        setActivoFilter([opcionActivo.value])
        onFilterChange()
    }

    const columnas: GridColDef<GroupUserViewDTO>[] = [
        {
            headerName: 'Usuario',
            field: GroupUserFields.FullName,
            flex: 3
        },
        {
            headerName: 'Email',
            field: GroupUserFields.Mail,
            flex: 3
        },
        {
            headerName: 'Cargo',
            field: GroupUserFields.Position,
            flex: 2
        },
        {
            field: 'actions',
            type: 'actions',
            width: 60,
            getActions: ({ row: user }) => {
                const userId = user[GroupUserFields.UserId]
                const isAssigned = (rowSelectionModel ?? []).includes(userId)

                if (!isAssigned) return []

                return [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title='Quitar del grupo'>
                                <RemoveCircleOutline fontSize='small' />
                            </Tooltip>
                        }
                        label='Quitar'
                        onClick={() => handleRemoveUser(userId)}
                        color='error'
                    />
                ]
            }
        }
    ]

    const groupName = group[GroupDTOFields.Name] ?? 'grupo'

    return (
        <>
            <DialogTitle sx={{ pb: 0 }}>
                <Stack direction='row' spacing={1.5} alignItems='center'>
                    <PeopleOutline color='primary' />
                    <Typography variant='h6' fontWeight={600}>
                        Usuarios de {groupName}
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                {/* Barra de filtros */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        alignItems: 'center',
                        mb: 2,
                        p: 2,
                        borderRadius: 1,
                        bgcolor: 'action.hover'
                    }}
                >
                    <Controller
                        control={control}
                        name={GroupUserFilterDTOFields.Nombre}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                value={field.value ?? ''}
                                label='Usuario'
                                size='small'
                                sx={{ minWidth: 160, flex: 1 }}
                                InputProps={{
                                    startAdornment: <Search fontSize='small' sx={{ mr: 0.5, color: 'text.secondary' }} />
                                }}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name={GroupUserFilterDTOFields.Mail}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                value={field.value ?? ''}
                                label='Email'
                                size='small'
                                sx={{ minWidth: 160, flex: 1 }}
                            />
                        )}
                    />
                    <Box sx={{ minWidth: 160, flex: 1 }}>
                        <ControlledAutocomplete
                            control={control}
                            name={GroupUserFilterDTOFields.CodsCargo}
                            label='Cargo'
                            valueField='id'
                            loadOptions={HttpCacheUser.getPositionList}
                            multiple
                        />
                    </Box>
                    <ToggleButtonGroup
                        size='small'
                        exclusive
                        value={activoFilter[0] ?? null}
                        onChange={handleActivoChange}
                    >
                        {toggleActivoOptions.map(option => (
                            <ToggleButton key={option.value} value={option.value} color={option.color}>
                                {option.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    <Button variant='contained' size='small' onClick={onFilterChange}>
                        Buscar
                    </Button>
                    <Tooltip title='Limpiar filtros'>
                        <IconButton size='small' onClick={handleClearFilters}>
                            <FilterAltOff fontSize='small' />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Grilla de usuarios */}
                <Box sx={{ height: '100%' }}>
                    <DataGrid
                        disableColumnFilter
                        disableColumnMenu
                        keepNonExistentRowsSelected
                        columns={columnas}
                        checkboxSelection
                        rowSelectionModel={rowSelectionModel}
                        onRowSelectionModelChange={setRowSelectionModel}
                        rows={users ?? []}
                        getRowId={row => row[GroupUserFields.UserId]}
                        density='compact'
                        pagination
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25, page: 0 }
                            }
                        }}
                        pageSizeOptions={[25, 50, 75, 100]}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button variant='text' color='inherit' onClick={onClose}>
                    Cancelar
                </Button>
                <Button variant='contained' color='primary' promise={onSubmitWrapper}>
                    Guardar
                </Button>
            </DialogActions>
        </>
    )
}

export default EditGroupUsers
